import { LightMode, RiksTVProvider } from '@rikstv/shared-components';
import { captureMessage } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useFeatureFLags } from '~/hooks/useFeatureFlags';
import { MaintenanceMode } from './MaintenanceMode';
import { Chatbot } from './chatbot/Chatbot';
import { FeatureFlagControlPanel } from './featureflags/feature-flag-panel';
import Header from './header/Header';
import { Modal } from './modal/Modal';
import OnRouteChange from './on-route-change/OnRouteChange';
import { CustomerVerificationCallback } from './routes/callback/CustomerVerificationCallback';
import { Campaign } from './routes/campaign/Campaign';
import { SelectSubscription } from './routes/campaign/SelectSubscription';
import { campaignRoutes, useFlowSteps } from './routes/campaign/campaign.routes';
import { InternetLandingPage, Route_InternetLandingPage } from './routes/internet/InternetLandingPage';
import { InternetOfferSubPage, Route_InternetOfferSubPage } from './routes/internet/InternetOfferSubPage';
import NotFound from './routes/not-found/NotFound';
import Receipt from './routes/receipt/Receipt';
import { Loader } from './shared';

const App: React.FC = () => {
  const { redirecting } = useRedirectService();
  const { rtvWebInternetSales } = useFeatureFLags();
  const flowsteps = useFlowSteps();

  return (
    <RiksTVProvider>
      <LightMode>
        <div id="app">
          <MaintenanceMode>
            <OnRouteChange>
              <Modal>
                <Header />
                <Routes>
                  <Route path="/callback" element={<CustomerVerificationCallback />} />
                  <Route path="/bekreftelse" element={<Receipt />} />
                  {/* Internet package deals flow */}
                  {rtvWebInternetSales && (
                    <Route path={Route_InternetLandingPage}>
                      <Route index element={<InternetLandingPage />} />
                      <Route path={Route_InternetOfferSubPage} element={<InternetOfferSubPage />} />
                    </Route>
                  )}
                  {/* Regular sales flow*/}
                  <Route path={campaignRoutes.index}>
                    <Route index element={<SelectSubscription />} />
                    <Route path={campaignRoutes.campaign} element={<Campaign />}>
                      {flowsteps.map((step) => (
                        <Route key={`flow-step-${step.stepPath}`} path={step.stepPath} element={step.element} />
                      ))}
                    </Route>
                  </Route>
                  {redirecting && <Route path="*" element={<Loader />} />}
                  {!redirecting && <Route path="*" element={<NotFound />} />}
                </Routes>
              </Modal>
            </OnRouteChange>
          </MaintenanceMode>
          <FeatureFlagControlPanel />
          <Chatbot />
        </div>
      </LightMode>
    </RiksTVProvider>
  );
};

const useRedirectService = () => {
  const navigate = useNavigate();
  const to = getPotentialRedirectUrl();
  const [redirecting, setRedirecting] = useState(to != null);

  useEffect(() => {
    if (to) {
      captureMessage('Redirect from old or deprecated campaign url', {
        level: 'info',
        fingerprint: ['redirect'],
        tags: { redirectTo: to, redirectFrom: globalThis.location.pathname },
      });
      navigate(to, { replace: true })?.then(() => {
        setRedirecting(false);
      });
    }
  }, [to, navigate]);

  return { redirecting };
};

const getPotentialRedirectUrl = () => {
  const pathSegments = globalThis.location.pathname.split('/').filter(Boolean);
  return getModernFlowRedirectUrl(pathSegments);
};

const validCampaignUrls = ['rikspakken', 'sportspakken', 'fleksipakken', 'strommepakken'];
// const validPartnerAliases = ['kampanje', 'homenet', 'gp', 'nextgentel'];
const validCustomerTypes = ['bolig', 'borettslag'];

const getModernFlowRedirectUrl = (pathSegments: string[]): string | undefined => {
  if (pathSegments.length === 0) {
    // we're on "/"
    return undefined;
  }
  const [_segmentOne, segmentTwo, segmentThree] = pathSegments;
  if (_segmentOne === campaignRoutes.index.replace('/', '')) {
    return undefined;
  }
  const validCampaignInSegmentTwo = validCampaignUrls.includes(segmentTwo);

  if (validCampaignInSegmentTwo) {
    return `${campaignRoutes.index}${segmentTwo}`;
  }
  const validCampaignInSegmentThree = validCampaignUrls.includes(segmentThree);
  if (validCampaignInSegmentThree) {
    return `${campaignRoutes.index}${segmentThree}`;
  }
  const validCustomerTypeInSegmentTwo = validCustomerTypes.includes(segmentTwo);
  if (validCustomerTypeInSegmentTwo) {
    return `${campaignRoutes.index}`;
  }
};

export default App;
